/** Pure parts of the category combobox: slugs, ranking the suggestions, the "Use …" row, the query cache. */
import type { CategorySuggestion } from '../../net/channel-api';

/** "Just Chatting" → "just-chatting": the slug form the control plane matches categories by. */
export function categorySlugOf(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\+/g, ' plus ')
    .replace(/#/g, ' sharp ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export type CategoryRow = { kind: 'category'; category: CategorySuggestion } | { kind: 'create'; name: string };

/** Whether `category` is what `text` names: the same name ignoring case, or the same slug. */
export function isExactMatch(text: string, category: Pick<CategorySuggestion, 'name' | 'slug'>): boolean {
  const typed = text.trim();
  if (typed === '') return false;
  if (typed.toLowerCase() === category.name.trim().toLowerCase()) return true;
  const slug = categorySlugOf(typed);
  return slug !== '' && slug === category.slug;
}

/**
 * The rows the list shows for `text`: suggestions without duplicate slugs, the exact match first,
 * then names starting with the text, then the rest in the server's order; and a final
 * "Use <text>" row when none of them is an exact match.
 */
export function categoryRows(text: string, suggestions: readonly CategorySuggestion[]): CategoryRow[] {
  const typed = text.trim();
  const seen = new Set<string>();
  const unique = suggestions.filter((c) => (seen.has(c.slug) ? false : (seen.add(c.slug), true)));
  const lower = typed.toLowerCase();
  const rank = (c: CategorySuggestion): number => (isExactMatch(typed, c) ? 0 : c.name.toLowerCase().startsWith(lower) ? 1 : 2);
  const ranked = unique.map((c, i) => ({ c, i, r: rank(c) })).sort((a, b) => a.r - b.r || a.i - b.i);
  const rows: CategoryRow[] = ranked.map(({ c }) => ({ kind: 'category', category: c }));
  if (typed !== '' && !unique.some((c) => isExactMatch(typed, c))) rows.push({ kind: 'create', name: typed });
  return rows;
}

/** `n` with a thousands separator: "live · 1,234 viewers". */
export function liveHint(viewerCount: number): string | null {
  if (!(viewerCount > 0)) return null;
  return `live · ${viewerCount.toLocaleString('en-US')} ${viewerCount === 1 ? 'viewer' : 'viewers'}`;
}

/** The last `capacity` search results, by API and query; the oldest is dropped first. */
export class QueryCache<T> {
  private readonly entries = new Map<string, T>();
  constructor(private readonly capacity = 50) {}

  static key(apiUrl: string, query: string): string {
    return `${apiUrl.trim().replace(/\/+$/, '')}\n${query.trim().toLowerCase()}`;
  }

  get(key: string): T | undefined {
    const value = this.entries.get(key);
    if (value !== undefined) {
      this.entries.delete(key);
      this.entries.set(key, value);
    }
    return value;
  }

  set(key: string, value: T): void {
    this.entries.delete(key);
    this.entries.set(key, value);
    while (this.entries.size > this.capacity) this.entries.delete(this.entries.keys().next().value!);
  }

  get size(): number {
    return this.entries.size;
  }

  clear(): void {
    this.entries.clear();
  }
}
